import * as React from "react";
import { Grid, FormControlLabel, FormGroup, Typography, TextField, Chip } from "@mui/material";
import { Formik } from "formik";
import { useMemo, useState } from "react";
import * as yup from "yup";
import OZInput, { sanitizeInput } from "../../../components/admin/common/OZInput";
import { ADMIN_FORM_MODES } from "../../../utils/constants";
import OZDialog from "../../../components/admin/common/OZDialog";
import CustomizedCheckbox from "../../../components/admin/common/CustomCheckBox";
import { Autocomplete, createFilterOptions } from "@mui/material";
import { showErrorNotification } from "../../../components/admin/common/NotiService";

const CustomTagsForm = (customTagFormProps) => {
    let customTagFormInitialMode = customTagFormProps.data ? "Edit" : ADMIN_FORM_MODES.add;
    customTagFormInitialMode = customTagFormProps?.openDialog === true ? "Edit" : customTagFormInitialMode;
    const [openAddDialog, setOpenAddDialog] = useState(true);
    // eslint-disable-next-line no-unused-vars
    const [customTagFormMode, setCustomTagFormMode] = useState(customTagFormInitialMode);

    const validationSchema = useMemo(
        () =>
            yup.object({
                tagName: yup
                    .string("Enter Tag Name")
                    .required("Tag Name is required")
                    .min(3, "Tag Name should be between 3 to 50 characters long")
                    .max(50, "Tag Name should be between 3 to 50 characters long")
                    .matches(/^[a-zA-Z_$][a-zA-Z0-9_$]*$/, "Tag Name must be a valid identifier: start with letter/underscore/$, contain only letters, numbers, and underscores"),
                tagValue: yup
                    .array()
                    .of(yup.string().required("Tag Value is required"))
                    .min(1, "At least one tag value is required")
            }),
        []
    );

    const closeCustomTagForm = () => {
        customTagFormProps.onClose();
        setOpenAddDialog(false);
    };

    const DataForm = (formProps) => {
        let customTagFormInitialData = formProps.editData
            ? formProps.editData
            : {
                tagName: "",
                tagValue: [],
                isActive: true,
            };

        // For filter options in autocomplete
        const filter = createFilterOptions();

        return (
            <div className="ozCustomTagAddForm">
                <Formik
                    initialValues={customTagFormInitialData}
                    validationSchema={validationSchema}
                    onSubmit={(userEnteredValues) => {
                        const formValues = { ...userEnteredValues };
                        console.log("userEnteredValues", userEnteredValues);
                        if (formValues && typeof formValues === "object" && "tagValue" in formValues) {
                            formValues.tagValue = JSON.stringify([{
                                [formValues.tagName]: formValues.tagValue
                            }])
                        }
                        if (formValues.tagValue && formValues.tagValue.length > 2000) {
                            showErrorNotification("Tag Values combined should be less than 2000 characters");
                            return;
                        }
                        if (customTagFormMode === "Create") {
                            customTagFormProps.addCustomTag(formValues, closeCustomTagForm);
                        }
                        else {
                            customTagFormProps.updateCustomTag(formValues, closeCustomTagForm);
                        }
                    }}
                >
                    {(formik) => (
                        <div>
                            <form
                                onSubmit={formik.handleSubmit}
                                id="customTagForm"
                                className="ozcwScrollbar oz-custom-tag-form-container"
                            >
                                <Grid container={true} spacing={2}>
                                    <Grid item xs={12} sm={12} md={12} lg={12}>
                                        <OZInput
                                            name="tagName"
                                            placeholder="Enter Tag Name"
                                            label="Tag Name *"
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Typography
                                            fontSize='12px'
                                            fontWeight={400}
                                            lineHeight='14px'
                                            marginBottom={'6px'}
                                            marginLeft='1px'
                                            color="#212121"
                                            htmlFor="tagValue"
                                        >
                                            Tag Values *
                                        </Typography>

                                        <Autocomplete
                                            name="tagValue"
                                            multiple
                                            id="tagValue"
                                            options={[]}
                                            value={formik?.values?.tagValue || []}
                                            componentsProps={{
                                                paper: { sx: { border: "1px solid #D6D6D7", marginTop: "6px", borderRadius: "8px" } }
                                            }}
                                            onChange={(event, newValue) => {
                                                try {
                                                    // Process each value to handle "Add" items
                                                    const processedValues = newValue.map(item => {
                                                        // If the item is a string and starts with "Add ", extract just the value
                                                        let value = item;
                                                        if (typeof item === 'string' && item.startsWith('Add "') && item.endsWith('"')) {
                                                            value = item.substring(5, item.length - 1);
                                                        }
                                                        return value ? sanitizeInput(value) : value;
                                                    });
                                                    formik.setFieldValue('tagValue', processedValues);
                                                    formik.setFieldTouched('tagValue', true, false);
                                                } catch (error) {
                                                    console.error("Error processing tag values:", error);
                                                }
                                            }}
                                            freeSolo
                                            filterOptions={(options, params) => {
                                                const filtered = filter(options, params);
                                                const { inputValue } = params;

                                                // Suggest the creation of a new value
                                                const isExisting = options.some(option => inputValue === option) ||
                                                    formik.values.tagValue.includes(inputValue);
                                                if (inputValue !== '' && !isExisting) {
                                                    filtered.push(`Add "${inputValue}"`);
                                                }

                                                return filtered;
                                            }}
                                            renderTags={(value, getTagProps) =>
                                                value.map((option, index) => (
                                                    <Chip
                                                        key={index}
                                                        label={option}
                                                        {...getTagProps({ index })}
                                                        size="small"
                                                    />
                                                ))
                                            }
                                            renderOption={(props, option) => {
                                                // Handle "Add" option differently
                                                if (option.startsWith('Add "') && option.endsWith('"')) {
                                                    const newValue = option.substring(5, option.length - 1);
                                                    return (
                                                        <li {...props} style={{
                                                            ...props.style,
                                                            wordBreak: 'break-all'
                                                        }}>
                                                            Add "{newValue}"
                                                        </li>
                                                    );
                                                }

                                                return <li {...props}>{option}</li>;
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    placeholder="Search or add values"
                                                    variant="outlined"
                                                    fullWidth
                                                    size="small"
                                                    error={formik.touched.tagValue && Boolean(formik.errors.tagValue)}
                                                    helperText={formik.touched.tagValue && formik.errors.tagValue}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            fontSize: "14px",
                                                            color: "#3D4C5E",
                                                            borderRadius: '8px',
                                                            minHeight: "44px"
                                                        },
                                                        minHeight: "44px"
                                                    }}
                                                    onBlur={() => formik.setFieldTouched('tagValue', true)}
                                                />
                                            )}
                                            selectOnFocus
                                            clearOnBlur
                                            handleHomeEndKeys
                                            getOptionLabel={(option) => {
                                                // Handle "Add" option
                                                if (typeof option === 'string') {
                                                    if (option.startsWith('Add "') && option.endsWith('"')) {
                                                        return option.substring(5, option.length - 1);
                                                    }
                                                    return option;
                                                }
                                                return '';
                                            }}
                                        />
                                    </Grid>

                                    <Grid sx={{ marginLeft: '0.125rem', marginTop: '10px' }} item xs={6} sm={6} md={6} lg={6}>
                                        <FormGroup>
                                            <FormControlLabel
                                                sx={{
                                                    '& .MuiTypography-root': {
                                                        fontSize: "0.875rem",
                                                        color: '#212121',
                                                    }
                                                }}
                                                control={
                                                    <CustomizedCheckbox
                                                        name="isActive"
                                                        defaultChecked={formik.values.isActive}
                                                        onChange={(e) => {
                                                            formik.setFieldValue('isActive', e.target.checked);
                                                        }}
                                                    />
                                                }
                                                label="Active"
                                            />
                                        </FormGroup>
                                    </Grid>
                                </Grid>
                            </form>
                        </div>
                    )}
                </Formik>
            </div>
        );
    };

    return (
        <div>
            {customTagFormMode === ADMIN_FORM_MODES.add ? (
                <OZDialog
                    title="Add Custom Tag"
                    saveButtonProps={{
                        form: 'customTagForm',
                        type: 'submit'
                    }}
                    open={openAddDialog}
                    mode={customTagFormMode}
                    onClose={closeCustomTagForm}
                >
                    <DataForm />
                </OZDialog>
            ) : (
                <OZDialog
                    title="Edit Custom Tag"
                    saveButtonProps={{
                        form: 'customTagForm',
                        type: 'submit'
                    }}
                    open={openAddDialog}
                    mode={customTagFormMode}
                    onClose={closeCustomTagForm}
                    hideSaveButton={!(customTagFormProps.editAccess) || false}
                >
                    <DataForm editData={customTagFormProps.data} />
                </OZDialog>
            )}
        </div>
    );
};

export default CustomTagsForm; 